import * as L from 'leaflet'
import * as geojson from 'geojson'
import * as turf from '@turf/turf'
import * as utils from './utils'
import { type GeoLocation, type GeoCircleLocation } from '../tcm_service'
import FilterManager from '../tcm_filter_manager'

import BaseSelectionModeBehaviour from './selection_mode_behaviour'

type TurfJS = typeof turf

type TurfFeature = geojson.Feature<geojson.Polygon | geojson.MultiPolygon>
type CircleOption = Parameters<typeof turf.circle>[2]

export default class CircleSelectionModeBehaviour extends BaseSelectionModeBehaviour {
    locationList: GeoCircleLocation[] = []
    areaList: TurfFeature[] = []

    override onBeforeScopeUpdate() {
        this.scope.slider.visible = true
    }

    override applyFilter(mgr: FilterManager): void {
        mgr.localizacaoCircles.clear()
        this.areaList.forEach((feature) => {
            if (!feature) {
                return
            }

            if (!feature.geometry) {
                return
            }

            if (!feature.geometry.coordinates) {
                return
            }

            feature.geometry.coordinates.forEach((coordinate) => {
                if (!coordinate) {
                    return
                }

                const shape: GeoLocation[] = []
                for (const turfPoint of coordinate.values()) {
                    shape.push({
                        lat: turfPoint[1] as number,
                        lon: turfPoint[0] as number
                    })
                }
                mgr.localizacaoCircles.add(shape)
            })
        })
    }

    plotFromFilter(map: L.Map, mgr: FilterManager): void {
        this.removeAllLayers()
        this.locationList.length = 0
        for (const circlePoints of mgr.localizacaoCircles.values()) {
            const circle = fitCircle(circlePoints)
            if (circle) {
                circle.radius *= 100
                this.locationList.push(circle)
            }
        }
        this.doPreparePoints()
        this.doDraw(map)
        this.scope.update()
    }

    override clear() {
        super.clear()
        this.locationList.length = 0
        this.areaList.length = 0
        this.owner.filterManager.localizacaoCircles.clear()
    }

    override hasFilter() {
        return this.locationList.length > 0
    }

    addLocation(location: GeoLocation, radius?: number) {
        const newLocationList: GeoCircleLocation[] = []

        let lastCircleLocation: GeoCircleLocation | null = {
            ...location,
            radius: typeof radius !== 'undefined' ? radius : this.getDistanceInKmUnit() / 2
        }

        let found = false
        const point = turf.point([location.lon, location.lat])
        for (const otherLocation of this.locationList) {
            const center = [otherLocation.lon, otherLocation.lat]
            const radius = otherLocation.radius
            const options: CircleOption = { steps: 10, units: 'kilometers' }
            const circle = turf.circle(center, radius, options)

            if (!found && turf.booleanContains(circle as TurfFeature, point)) {
                lastCircleLocation = otherLocation
                lastCircleLocation.lat = location.lat
                lastCircleLocation.lon = location.lon
                found = true
                if (this.keyboard.ctrlKey) {
                    lastCircleLocation = null
                }
                continue
            }
            newLocationList.push(otherLocation)
        }

        if (lastCircleLocation) {
            newLocationList.push(lastCircleLocation)
            if (this.scope.slider.unit === 'm') {
                this.scope.slider.value = lastCircleLocation.radius * 1000 * 2
            } else if (this.scope.slider.unit === 'km') {
                this.scope.slider.value = lastCircleLocation.radius * 2
            }
        }

        this.locationList = newLocationList
    }

    override setZoom(value: number): void {
        // console.debug('setZoom: ' + value)
        const scope = this.scope
        if (value >= 15) {
            scope.slider.unit === 'km' && this.__setSliderValues('m')
        } else {
            scope.slider.unit === 'm' && this.__setSliderValues('km')
        }
    }

    override setMode(value: number): void {
        const scope = this.scope
        scope.mode = 'circle'

        if (value >= 15) {
            this.__setSliderValues('m')
        } else {
            this.__setSliderValues('km')
        }
    }

    private __setSliderValues(unit: string) {
        const scope = this.scope
        if (unit === 'm') {
            let value = 300
            if (scope.slider.unit === 'km') {
                value = scope.slider.value * 1000
            }

            scope.slider.unit = 'm'
            scope.slider.min = 10
            scope.slider.max = 10000
            scope.slider.step = 10
            scope.slider.value = value
            return
        }

        if (unit === 'km') {
            let value = 0.5
            if (scope.slider.unit === 'm') {
                value = scope.slider.value / 1000
            }
            scope.slider.unit = 'km'
            scope.slider.min = 0.1
            scope.slider.max = 100
            scope.slider.step = 0.1
            scope.slider.value = value
            return
        }
    }

    preparePoints() {
        if (this.locationList.length > 0) {
            const p = this.locationList[this.locationList.length - 1]
            p.radius = this.getDistanceInKmUnit() / 2
        }

        this.doPreparePoints()
    }

    private doPreparePoints() {
        const turfJs = turf as TurfJS

        this.areaList.length = 0
        let circlePolygonList: (TurfFeature | null)[] = []
        for (const otherLocation of this.locationList) {
            const center = [otherLocation.lon, otherLocation.lat]
            const radius = otherLocation.radius

            let steps = 10
            if (radius >= 10) {
                steps = 30
            } else if (radius >= 1) {
                steps = 20
            }

            const options: CircleOption = { steps, units: 'kilometers' }
            const circle = turf.circle(center, radius, options)

            circlePolygonList.push(circle as TurfFeature)
        }

        this.areaList = circlePolygonList as TurfFeature[]

        let hasIntersection = true
        while (hasIntersection) {
            hasIntersection = false
            let refPolygon = circlePolygonList[0]!

            const newCirclePolygonList: TurfFeature[] = []
            newCirclePolygonList.push(refPolygon)
            for (let i = 1; i < circlePolygonList.length; i++) {
                const somePolygon = circlePolygonList[i]!

                if (turfJs.booleanIntersects(refPolygon, somePolygon)) {
                    const unitedPolygon = turf.union(turf.featureCollection([refPolygon, somePolygon]))
                    if (unitedPolygon) {
                        refPolygon = unitedPolygon
                        newCirclePolygonList[0] = refPolygon
                    }
                    hasIntersection = true
                    circlePolygonList[i] = null
                } else {
                    newCirclePolygonList.push(somePolygon)
                }
            }

            circlePolygonList = newCirclePolygonList
        }

        this.areaList = circlePolygonList as TurfFeature[]
    }

    override draw(map: L.Map): void {
        this.removeAllLayers()
        if (this.locationList.length === 0) {
            return
        }

        this.doDraw(map)
    }

    doDraw(map: L.Map) {
        try {
            this.doDrawCircles(map)
            //this.doDrawArea(map)
        } catch (e) {
            console.error('doDraw', e)
        }
    }

    doDrawArea(map: L.Map) {
        if (this.areaList.length === 0) {
            //this.removeAllLayers = utils.staticNoopOther
            return
        }

        const multiPolygons: L.LatLngLiteral[][] = []

        for (const feature of this.areaList) {
            for (const coordinate of feature.geometry.coordinates) {
                const mapPolygon: L.LatLngLiteral[] = []

                coordinate.forEach((v) => {
                    mapPolygon.push({
                        lat: v[1] as number,
                        lng: v[0] as number
                    })
                })

                multiPolygons.push(mapPolygon)
            }
        }

        const areaLayer = L.polygon(multiPolygons, {
            fill: false
        }).addTo(map)

        const oldRemoveAllLayers = this.removeAllLayers
        this.removeAllLayers = () => {
            oldRemoveAllLayers()
            areaLayer.removeFrom(map)
            this.removeAllLayers = utils.staticNoopOther
        }
    }

    doDrawCircles(map: L.Map) {
        const shapeArray: L.Layer[] = []
        for (let i = 0, iLast = this.locationList.length - 1; i <= iLast; i++) {
            const p1 = this.locationList[i]

            const color = i < iLast ? utils.NON_ACTIVE_SELECTION_COLOR : utils.ACTIVE_SELECTION_COLOR

            shapeArray.push(
                L.circle([p1.lat, p1.lon], {
                    radius: p1.radius * 1000,
                    color,
                    stroke: false,
                    fillOpacity: 0.4
                }).addTo(map)
            )
        }

        const oldRemoveAllLayers = this.removeAllLayers
        this.removeAllLayers = () => {
            oldRemoveAllLayers()
            for (const shape of shapeArray) {
                shape.removeFrom(map)
            }
            this.removeAllLayers = utils.staticNoopOther
        }
    }
}

// Algoritmo para compular um circulo a partir de pontos

function fitCircle(points: GeoLocation[]): GeoCircleLocation | null {
    if (points.length < 3) {
        return null
    }

    // Montar matriz A e vetor b
    const A: number[][] = points.map((p) => [p.lat, p.lon, 1])
    const b: number[] = points.map((p) => -(p.lat * p.lat + p.lon * p.lon))

    // Resolver o sistema de mínimos quadrados: v = (A^T A)^(-1) A^T b
    const v = solveLeastSquares(A, b)

    // Extrair D, E, F
    const [D, E, F] = v

    // Calcular centro (h, k) e raio
    const h = -D / 2
    const k = -E / 2
    const radius = Math.sqrt(h * h + k * k - F)

    if (isNaN(radius)) {
        throw new Error('Não foi possível ajustar um círculo (raio inválido).')
    }

    return {
        lat: h,
        lon: k,
        radius
    }
}

/// Função para resolver o sistema de mínimos quadrados
function solveLeastSquares(A: number[][], b: number[]): number[] {
    // Calcular A^T A e A^T b
    const At = transpose(A)
    const AtA = matrixMultiply(At, A)
    const Atb = matrixVectorMultiply(At, b)

    // Resolver o sistema AtA * v = Atb usando eliminação de Gauss
    return solveLinearSystem(AtA, Atb)
}

// Transpor uma matriz
function transpose(matrix: number[][]): number[][] {
    const rows = matrix.length
    const cols = matrix[0].length
    const result: number[][] = Array(cols)
        .fill(0)
        .map(() => Array(rows).fill(0))
    for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
            result[j][i] = matrix[i][j]
        }
    }
    return result
}

// Multiplicação de matrizes
function matrixMultiply(A: number[][], B: number[][]): number[][] {
    const rowsA = A.length
    const colsA = A[0].length
    const colsB = B[0].length
    const result: number[][] = Array(rowsA)
        .fill(0)
        .map(() => Array(colsB).fill(0))
    for (let i = 0; i < rowsA; i++) {
        for (let j = 0; j < colsB; j++) {
            for (let k = 0; k < colsA; k++) {
                result[i][j] += A[i][k] * B[k][j]
            }
        }
    }
    return result
}

// Multiplicação de matriz por vetor
function matrixVectorMultiply(A: number[][], b: number[]): number[] {
    const rows = A.length
    const cols = A[0].length
    const result: number[] = Array(rows).fill(0)
    for (let i = 0; i < rows; i++) {
        for (let j = 0; j < cols; j++) {
            result[i] += A[i][j] * b[j]
        }
    }
    return result
}

// Resolver sistema linear Ax = b usando eliminação de Gauss
function solveLinearSystem(A: number[][], b: number[]): number[] {
    const n = A.length
    const augmented: number[][] = A.map((row, i) => [...row, b[i]])

    // Eliminação de Gauss
    for (let i = 0; i < n; i++) {
        // Encontrar pivô
        let maxRow = i
        for (let k = i + 1; k < n; k++) {
            if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
                maxRow = k
            }
        }

        // Swap
        [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]]

        // Tornar o pivô igual a 1
        const pivot = augmented[i][i]
        if (Math.abs(pivot) < 1e-10) {
            throw new Error('Sistema singular ou mal condicionado.')
        }
        for (let j = i; j <= n; j++) {
            augmented[i][j] /= pivot
        }

        // Eliminar coluna
        for (let k = 0; k < n; k++) {
            if (k !== i) {
                const factor = augmented[k][i]
                for (let j = i; j <= n; j++) {
                    augmented[k][j] -= factor * augmented[i][j]
                }
            }
        }
    }

    // Extrair solução
    return augmented.map((row) => row[n])
}
